import {
  visualSceneOf,
  visualAnswersMatch,
  type VisualScene,
} from "./yufa-visual.js";
import type { ItemWriting } from "./yufa-written-item.js";
import { recordOf, writingOf } from "./yufa-writing-read.js";
import { listeningPromptOf, type ListeningPrompt } from "./yufa-listening.js";
import {
  answerKey,
  grammarAnswer,
  hasText,
  type GrammarTeaching,
} from "./yufa-teaching.js";

/** Each option declares how its text is rendered and compared. */
export type MeaningOption =
  | { readonly language: "zh"; readonly writing: ItemWriting }
  | { readonly language: "en"; readonly text: string };

/** The optional given sentence is part of the visible question in both scripts. */
interface MeaningFields {
  readonly listening?: ListeningPrompt;
  readonly visual?: VisualScene;
  readonly given?: ItemWriting;
  readonly options: readonly MeaningOption[];
  readonly accepted: readonly number[];
}
export type MeaningBody = MeaningFields &
  ({ readonly kind: "interpretation" } | { readonly kind: "reply" });

/** Choice values follow the authored order across builds and reader settings. */
export function meaningText(option: MeaningOption): string {
  return option.language === "en" ? option.text : option.writing.simplified;
}

/** Preserve English verbatim and require both scripts for Chinese. */
function optionOf(value: unknown): MeaningOption | undefined {
  const record = recordOf(value);
  if (record?.["language"] === "en") {
    return hasText(record["text"]) &&
      !/\p{Script=Han}/u.test(record["text"]) &&
      record["writing"] === undefined
      ? { language: "en", text: record["text"] }
      : undefined;
  }
  const writing = writingOf(record?.["writing"]);
  return record?.["language"] === "zh" &&
    writing !== undefined &&
    record["text"] === undefined &&
    [writing.simplified, writing.traditional].every((text) =>
      /\p{Script=Han}/u.test(text),
    )
    ? { language: "zh", writing }
    : undefined;
}

/** Reject absent keys, duplicate choices and hidden or malformed source sentences. */
export function meaningBodyOf(value: unknown): MeaningBody | undefined {
  const record = recordOf(value);
  if (
    record === undefined ||
    (record["kind"] !== "interpretation" && record["kind"] !== "reply")
  ) {
    return undefined;
  }
  const options = Array.isArray(record["options"])
    ? record["options"].map(optionOf)
    : [];
  const accepted = record["accepted"];
  const given = writingOf(record["given"]);
  const listening = listeningPromptOf(record["listening"], given);
  const visual = visualSceneOf(record["visual"]);
  if (
    (record["visual"] !== undefined &&
      (visual === undefined ||
        record["kind"] !== "interpretation" ||
        record["given"] !== undefined ||
        record["listening"] !== undefined)) ||
    (record["listening"] !== undefined && listening === undefined) ||
    options.length < 2 ||
    options.length > 4 ||
    !options.every((option) => option !== undefined) ||
    !Array.isArray(accepted) ||
    accepted.length === 0 ||
    new Set(accepted).size !== accepted.length ||
    !accepted.every(
      (at) => Number.isInteger(at) && at >= 0 && at < options.length,
    ) ||
    (record["given"] !== undefined &&
      (given === undefined ||
        ![given.simplified, given.traditional].every((text) =>
          /\p{Script=Han}/u.test(text),
        ))) ||
    (given === undefined && options.every((option) => option.language === "en"))
  ) {
    return undefined;
  }
  if (visual !== undefined && !visualAnswersMatch(visual, options, accepted)) {
    return undefined;
  }
  const held = new Map<string, number>();
  for (const [at, option] of options.entries()) {
    const texts =
      option.language === "en"
        ? [option.text]
        : [option.writing.simplified, option.writing.traditional];
    for (const text of texts) {
      const key = `${option.language}:${answerKey(text, option.language)}`;
      if (held.has(key) && held.get(key) !== at) {
        return undefined;
      }
      held.set(key, at);
    }
  }
  return {
    kind: record["kind"],
    options,
    accepted,
    ...(listening === undefined ? {} : { listening }),
    ...(visual === undefined ? {} : { visual }),
    ...(given === undefined ? {} : { given }),
  };
}

/** Every choice needs an explicit language, status and explanation tied to the exact key. */
export function meaningTeachingMatches(
  item: MeaningBody,
  teaching: GrammarTeaching | undefined,
): boolean {
  if (
    teaching === undefined ||
    teaching.task !==
      (item.kind === "interpretation" ? "interpret" : "reply") ||
    teaching.answers.length !== item.options.length
  ) {
    return false;
  }
  const ids = new Set<string>();
  return item.options.every((option, at) => {
    const answer = grammarAnswer(
      teaching,
      meaningText(option),
      option.language,
    );
    if (
      answer === undefined ||
      answer.language !== option.language ||
      answer.status === "unverified" ||
      ids.has(answer.id) ||
      (answer.status === "target") !== item.accepted.includes(at)
    ) {
      return false;
    }
    ids.add(answer.id);
    return answer.language === "en"
      ? option.language === "en" &&
          answer.forms.length === 1 &&
          answer.forms[0] === option.text
      : option.language === "zh" &&
          answer.forms.length === 1 &&
          answer.forms[0]!.simplified === option.writing.simplified &&
          answer.forms[0]!.traditional === option.writing.traditional;
  });
}
